import { useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { CommandPalette } from '../common/CommandPalette';
import { palette, fontFamily, admin } from '../../styles/tokens';
import type { Business, User } from '../../types';

interface AppShellProps {
  business: Business;
  user: User;
  overdueCount?: number;
}

function DesktopGuard({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!isMobile) return <>{children}</>;

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: palette.bg, fontFamily, padding: 20,
    }}>
      <div style={{
        maxWidth: 400, width: '100%', textAlign: 'center', padding: 40,
        background: palette.white, borderRadius: 12, border: `1px solid ${palette.g150}`,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, margin: '0 auto 16px',
          background: `linear-gradient(135deg, ${palette.accent}, ${palette.accentDeep})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: palette.g800, marginBottom: 8 }}>
          Crew<span style={{ color: palette.accent }}>Bit</span> Admin
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: 20 }}>
          The Admin Panel is designed for desktop use. Please open this on a computer, or use the CrewBit mobile app for on-the-go management.
        </div>
        <button onClick={() => window.open('https://crewbit.ca', '_blank')} style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          background: palette.accent, color: palette.white,
          fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>Download the App</button>
      </div>
    </div>
  );
}

export function AppShell({ business, user, overdueCount }: AppShellProps) {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('crewbit-sidebar-collapsed') === 'true'; }
    catch { return false; }
  });
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('crewbit-sidebar-collapsed', String(next)); } catch { /* localStorage unavailable */ }
      return next;
    });
  };

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && paletteOpen) {
        e.preventDefault();
        closePalette();
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        if (e.key === 'k') {
          e.preventDefault();
          setPaletteOpen(prev => !prev);
          return;
        }
        if (e.key === ',') {
          e.preventDefault();
          void navigate('/settings');
          return;
        }
        const routes: Record<string, string> = {
          '1': '/today', '2': '/schedule', '3': '/team', '4': '/money', '5': '/reports',
        };
        const route = routes[e.key];
        if (route) { e.preventDefault(); void navigate(route); }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, paletteOpen, closePalette]);

  return (
    <DesktopGuard>
      <div style={{
        display: 'flex', minHeight: '100vh',
        background: admin.bg,
        fontFamily,
        WebkitFontSmoothing: 'antialiased',
      }}>
        <Sidebar user={user} overdueCount={overdueCount} collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <TopBar onSearchClick={openPalette} />
          <div style={{
            padding: '24px', maxWidth: 1400, width: '100%',
            animation: 'crewbit-fadein 150ms ease',
          }}>
            <style>{`@keyframes crewbit-fadein { from { opacity: 0; } to { opacity: 1; } }`}</style>
            <Outlet />
          </div>
        </div>
      </div>
      <CommandPalette isOpen={paletteOpen} onClose={closePalette} businessId={business.id} />
    </DesktopGuard>
  );
}
